import * as React from "react"
import { useEffect } from "react"
import { Route, Switch } from "react-router-dom"
import { useUpdateEffect } from "react-use"
import { type ApolloClient, ApolloProvider, type NormalizedCacheObject } from "@apollo/client"
import { ModuleLoading } from "@digits-shared/components/Loaders"
import { ProtectedRoute } from "@digits-shared/components/Router/ProtectedRoute"
import envHelper from "@digits-shared/helpers/envHelper"
import useConstant from "@digits-shared/hooks/useConstant"
import useSession from "@digits-shared/hooks/useSession"
import { useSessionListeners } from "@digits-shared/session/useSessionListeners"
import {
  type ApolloClientOptions,
  setupApolloClient,
} from "src/shared/initializers/apollo/setupApolloClient"
import { AdminBearerRefresh } from "src/admin/components/Session/AdminBearerRefresh"
import LogOut from "src/admin/components/Session/LogOut"
import routes from "src/admin/routes"
import type AdminSession from "src/admin/session/AdminSession"
import ClientVersionTracker from "src/shared/components/Background/ClientVersionTracker"
import { TimeContextURLProvider } from "src/shared/components/Contexts/TimeContext"

const APOLLO_CLIENT_OPTIONS: ApolloClientOptions = {
  defaultFetchPolicy: "network-only",
  neverBatch: true,
  withWS: true,
}

/*
  COMPONENTS
*/

const LogIn = React.lazy(() => import("./Session/LogIn"))

const Home = React.lazy(() => import("./Home/Home"))

/**
 * Top-level app component for the admin portal
 */
export const AdminClient: React.FC = () => {
  useSessionListeners(routes.login.parameterizedPath)
  const session = useSession<AdminSession>()

  const initialClient = useConstant(() => setupApolloClient(session, APOLLO_CLIENT_OPTIONS))
  const [apolloClient, setApolloClient] =
    React.useState<ApolloClient<NormalizedCacheObject>>(initialClient)

  useUpdateEffect(() => {
    apolloClient.stop()
    setApolloClient(setupApolloClient(session, APOLLO_CLIENT_OPTIONS))
  }, [session.user])

  useEffect(() => {
    let title = "Digits Admin"

    if (envHelper.isDevelopment()) {
      title = "[Dev] " + title
    } else if (envHelper.isStaging()) {
      title = "[Staging] " + title
    }

    document.title = title
  }, [])

  return (
    <ApolloProvider client={apolloClient}>
      <TimeContextURLProvider routes={routes}>
        <AdminBearerRefresh />
        <ClientVersionTracker />
        <React.Suspense fallback={<ModuleLoading />}>
          <Switch>
            <Route exact path={routes.login.parameterizedPath} component={LogIn} />

            <Route
              exact
              path={routes.logout.parameterizedPath}
              render={() => (
                <ProtectedRoute component={LogOut} redirectTo={routes.login.parameterizedPath} />
              )}
            />

            <Route
              path="*"
              render={() => (
                <ProtectedRoute component={Home} redirectTo={routes.login.parameterizedPath} />
              )}
            />
          </Switch>
        </React.Suspense>
      </TimeContextURLProvider>
    </ApolloProvider>
  )
}
